"use client";

import { ArrowLeft, Calendar, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="h-12 bg-muted/30 animate-pulse mb-6 rounded-md" />
        <div className="h-6 bg-muted/30 animate-pulse mb-8 w-1/2 rounded-md" />
        <div className="h-96 bg-muted/30 animate-pulse mb-8 rounded-md" />
        <div className="space-y-3">
          <div className="h-4 bg-muted/30 animate-pulse rounded-md" />
          <div className="h-4 bg-muted/30 animate-pulse rounded-md" />
          <div className="h-4 bg-muted/30 animate-pulse w-3/4 rounded-md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <p className="text-muted-foreground">No ha sido posible cargar el artículo. Por favor, inténtelo de nuevo más tarde.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <p className="text-muted-foreground">Artículo no encontrado.</p>
        <Link to="/HomePage" className="mt-6 inline-block">
          <Button variant="outline" className="mt-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-lg">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al inicio
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("es-ES", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="max-w-4xl mx-auto">
      <header className="mb-10 md:mb-14">
        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {post.categories.map((category, i) => (
              <span
                data-index={i}
                key={category.id}
                className="text-xs uppercase tracking-widest font-default text-accent-foreground border border-accent-foreground/40 px-3 py-1"
              >
                {category.name}
              </span>
            ))}
          </div>
        )}

        <h1
          className="font-default font-semibold text-4xl md:text-5xl lg:text-6xl leading-tight text-foreground mb-8"
          dangerouslySetInnerHTML={{ __html: post.title }}
        />

        <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground border-t border-b border-border py-4">
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-accent-foreground" />
              <span className="font-default uppercase tracking-wider text-xs">
                {post.author.name}
              </span>
            </div>
          )}
          {formattedDate && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-accent-foreground" />
              <span className="font-default uppercase tracking-wider text-xs">
                {formattedDate}
              </span>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-12 md:mb-16 overflow-hidden rounded-lg shadow-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-cover"
          />
        </div>
      )}

      {post.content && (
        <div
          className="prose prose-lg max-w-none font-default text-foreground/90 leading-relaxed text-lg md:text-xl [&_p]:mb-6 [&_h2]:font-default [&_h2]:text-3xl [&_h2]:md:text-4xl [&_h2]:font-semibold [&_h2]:mt-12 [&_h2]:mb-6 [&_h2]:text-foreground [&_h3]:font-default [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:mt-10 [&_h3]:mb-4 [&_h3]:text-foreground [&_a]:text-accent-foreground [&_a]:underline [&_a]:underline-offset-4 [&_blockquote]:border-l-2 [&_blockquote]:border-accent-foreground [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:my-8 [&_blockquote]:text-muted-foreground [&_img]:my-8 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_li]:mb-2"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {post.tags && post.tags.length > 0 && (
        <div className="mt-16 pt-8 border-t border-border">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs uppercase tracking-widest font-default text-muted-foreground">
              Etiquetas
            </span>
            {post.tags.map((tag, i) => (
              <span
                data-index={i}
                key={tag.id}
                className="text-xs uppercase tracking-wider font-default text-foreground/70 border border-border px-3 py-1 hover:border-accent-foreground hover:text-accent-foreground transition-colors rounded-md"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-16 pt-8 border-t border-border flex items-center justify-between">
        <Link to="/BlogPage">
          <Button variant="outline" className="border-border hover:border-accent-foreground hover:text-accent-foreground rounded-lg">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Blog
          </Button>
        </Link>
        <div className="hidden md:block text-xs uppercase tracking-widest font-default text-muted-foreground">
          Antonio Heredia — Psicoanálisis
        </div>
      </div>
    </article>
  );
}

export default function Post() {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="5045"
      id="single-post-template-section"
      className="bg-background text-foreground py-16 md:py-24 px-4 md:px-8"
    >
      <div className="container mx-auto">
        <div className="mb-12 md:mb-16 max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-px w-12 bg-accent-foreground" />
            <span className="text-xs uppercase tracking-widest font-default text-accent-foreground">
              Publicaciones
            </span>
          </div>
        </div>

        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>
      </div>
    </section>
  );
}